// src/routes/organizationRoutes.js
// Routes accessible to organization user types

import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/authContext";

// Organization user type page imports
import OrganizationDashboard from "../pages/organization/dashboard";
import AllTeam from "../pages/organization/team/AllTeam";
import ShowTeam from "../pages/organization/team/ShowTeam";
import TeamFormPage from "../pages/organization/team/TeamFormPage";
import ShowMember from "../pages/organization/team/ShowMember";
import PlayerProfile from "../pages/organization/team/PlayerProfile";
import AssignSupervisor from "../pages/organization/team/AssignSupervisor";
import GetAllEvent from "../pages/organization/event/GetAllEvent";
import ShowEvent from "../pages/organization/event/ShowEvent";
import CreateEvent from "../pages/organization/event/CreateEvent";
import ShowAllTeamToCreateEvent from "../pages/organization/event/ShowAllTeamToCreateEvent";
import GetAllMatch from "../pages/organization/matches/GetAllMatch";
import CreateMatch from "../pages/organization/matches/createMatch";
import AllMember from "../pages/organization/members/AllMember";
import CreateMember from "../pages/organization/members/CreateMember";
import ShowMemberDetails from "../pages/organization/members/ShowMember";
import CreateRoleForMember from "../pages/organization/members/CreateRoleForMember";
import AllPlayers from "../pages/organization/allplayers";
import SubscriptionPackages from "../pages/organization/subscription/packages";
import CompetitionRequests from "../pages/organization/CompetitionRequests";
import CreateCompetitionRequest from "../pages/organization/CreateCompetitionRequest";
import CompetitionRequestDetail from "../pages/organization/CompetitionRequestDetail";

/**
 * Account Type Protection Wrapper
 * Checks if user has the correct account type (supervisor or organization)
 */
const OrganizationAccessRoute = ({ children }) => {
  const { user } = useAuth();

  // Check if user has correct account type
  const hasOrganizationAccess = () => {
    if (!user?.accountType) {
      return false;
    }

    const allowedAccountTypes = ['organization', 'supervisor'];
    return allowedAccountTypes.includes(user.accountType.toLowerCase());
  };

  // If unauthorized account type, redirect to appropriate dashboard
  if (!hasOrganizationAccess()) {
    console.log(
      `🚫 Access denied: Account type "${user?.accountType}" not authorized for organization routes. Redirecting...`
    );
    
    // Redirect based on account type
    switch (user?.accountType?.toLowerCase()) {
      case 'player':
        return <Navigate to="/player/dashboard" replace />;
      case 'parent':
        return <Navigate to="/parent/dashboard" replace />;
      case 'talent_scout':
        return <Navigate to="/talent-scout/dashboard" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  // If authorized account type, allow access
  return children;
};

/**
 * Subscription Protection Wrapper
 * Checks if user has valid subscription before allowing access to premium features
 */
const SubscriptionProtectedRoute = ({ children }) => {
  const { user } = useAuth();

  // Check if user has valid subscription
  const hasValidSubscription = () => {
    if (!user?.metaData?.subscription) {
      return false;
    }

    const subscription = user.metaData.subscription;

    // Check if subscription is active
    if (!subscription.isActive) {
      return false;
    }

    // Check if subscription hasn't expired
    if (subscription.endDate && new Date(subscription.endDate) <= new Date()) {
      return false;
    }

    return true;
  };

  // If no valid subscription, redirect to subscription packages
  if (!hasValidSubscription()) {
    console.log(
      "🚫 Access denied: No valid subscription. Redirecting to subscription packages."
    );
    return <Navigate to="/organization/subscription/packages" replace />;
  }

  // If valid subscription, allow access
  return children;
};

/**
 * Combined Protection Wrapper
 * Checks both account type and subscription requirements
 */
const FullProtectedRoute = ({ children }) => {
  return (
    <OrganizationAccessRoute>
      <SubscriptionProtectedRoute>
        {children}
      </SubscriptionProtectedRoute>
    </OrganizationAccessRoute>
  );
};

export const organizationRoutes = [
  // Subscription management - Account type check only (no subscription required)
  {
    path: "/organization/subscription/packages",
    element: (
      <OrganizationAccessRoute>
        <SubscriptionPackages />
      </OrganizationAccessRoute>
    ),
  },

  // Dashboard - Account type check only (no subscription required)
  {
    path: "/organization/dashboard",
    element: (
      <OrganizationAccessRoute>
        <OrganizationDashboard />
      </OrganizationAccessRoute>
    ),
  },

  // Team management - Requires account type and subscription
  {
    path: "/organization/team/all",
    element: (
      <OrganizationAccessRoute>
        <AllTeam />
      </OrganizationAccessRoute>
    ),
  },
  {
    path: "/organization/team/:teamId",
    element: (
      <OrganizationAccessRoute>
        <ShowTeam />
      </OrganizationAccessRoute>
    ),
  },
  {
    path: "/organization/team/create",
    element: (
      <FullProtectedRoute>
        <TeamFormPage />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/team/edit/:teamId",
    element: (
      <FullProtectedRoute>
        <TeamFormPage />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/team/:teamId/member/:memberId",
    element: (
      <FullProtectedRoute>
        <ShowMember />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/team/player/:playerId",
    element: (
      <FullProtectedRoute>
        <PlayerProfile />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/team/assign-supervisor/:teamId",
    element: (
      <FullProtectedRoute>
        <AssignSupervisor />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/team/assign-supervisor-edit/:memberId/:teamId",
    element: (
      <FullProtectedRoute>
        <AssignSupervisor />
      </FullProtectedRoute>
    ),
  },

  // Event management - Requires account type and subscription
  {
    path: "/organization/events",
    element: (
      <FullProtectedRoute>
        <GetAllEvent />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/events/:eventId",
    element: (
      <FullProtectedRoute>
        <ShowEvent />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/events/create",
    element: (
      <FullProtectedRoute>
        <CreateEvent />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/events/create/team/:teamId",
    element: (
      <FullProtectedRoute>
        <CreateEvent />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/event/edit/:eventId",
    element: (
      <FullProtectedRoute>
        <CreateEvent />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/events/show-teams",
    element: (
      <FullProtectedRoute>
        <ShowAllTeamToCreateEvent />
      </FullProtectedRoute>
    ),
  },

  // Match management - Requires account type and subscription
  {
    path: "/organization/matches",
    element: (
      <FullProtectedRoute>
        <GetAllMatch />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/matches/create",
    element: (
      <FullProtectedRoute>
        <CreateMatch />
      </FullProtectedRoute>
    ),
  },

  // Member management - Requires account type and subscription
  {
    path: "/organization/members",
    element: (
      <FullProtectedRoute>
        <AllMember />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/members/create",
    element: (
      <FullProtectedRoute>
        <CreateMember />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/members/:memberId",
    element: (
      <FullProtectedRoute>
        <ShowMemberDetails />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/members/create-role/:memberId",
    element: (
      <FullProtectedRoute>
        <CreateRoleForMember />
      </FullProtectedRoute>
    ),
  },

  // Player management - Requires account type and subscription
  {
    path: "/organization/players",
    element: (
      <FullProtectedRoute>
        <AllPlayers />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/player/:playerId",
    element: (
      <FullProtectedRoute>
        <PlayerProfile />
      </FullProtectedRoute>
    ),
  },

  // Competition requests - Requires account type and subscription
  {
    path: "/organization/competition-requests",
    element: (
      <FullProtectedRoute>
        <CompetitionRequests />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/competition-requests/create",
    element: (
      <FullProtectedRoute>
        <CreateCompetitionRequest />
      </FullProtectedRoute>
    ),
  },
  {
    path: "/organization/competition-requests/:requestId",
    element: (
      <FullProtectedRoute>
        <CompetitionRequestDetail />
      </FullProtectedRoute>
    ),
  },
];
